import { useEffect, useRef } from 'react';

import { COOKIE_CATEGORIES } from '../config/categories';
import type { ConsentState, CookieCategory, CookieCategoryConfig, CookieConsentUiStrings } from '../types/cookies.types';

interface CookieModalProps {
  open: boolean;
  draft: ConsentState;
  categories?: CookieCategoryConfig[];
  ui?: Partial<CookieConsentUiStrings>;
  onClose: () => void;
  onToggle: (category: CookieCategory, value: boolean) => void;
  onSave: () => void;
  onAcceptAll: () => void;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible preferences modal with per-category toggles.
 */
export function CookieModal({
  open,
  draft,
  categories,
  ui = {},
  onClose,
  onToggle,
  onSave,
  onAcceptAll,
}: CookieModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const cats = categories?.length ? categories : COOKIE_CATEGORIES;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusable.length === 0) {
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }, 50);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const enableLabel = (label: string) =>
    (ui.enableCategory ?? 'Enable {label}').replace('{label}', label);

  return (
    <div className="cookie-modal" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className="cookie-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cookie-modal__header">
          <h2 id="cookie-modal-title" className="cookie-modal__title">
            {ui.modalTitle ?? ui.settingsTitle ?? 'Cookie preferences'}
          </h2>
          <button
            type="button"
            className="cookie-modal__close"
            aria-label={ui.close ?? 'Close'}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="cookie-modal__body">
          {cats.map((cat) => (
            <section key={cat.id} className="cookie-modal__category">
              <div className="cookie-modal__category-head">
                <div>
                  <h3 className="cookie-modal__category-title">{cat.label}</h3>
                  <p className="cookie-modal__category-desc">{cat.description}</p>
                </div>
                {cat.required ? (
                  <span className="cookie-modal__badge">{ui.alwaysActive ?? 'Always active'}</span>
                ) : (
                  <label className="cookie-toggle" aria-label={enableLabel(cat.label)}>
                    <input
                      type="checkbox"
                      checked={draft[cat.id]}
                      onChange={(e) => onToggle(cat.id, e.target.checked)}
                    />
                    <span className="cookie-toggle__slider" />
                  </label>
                )}
              </div>
              <ul className="cookie-modal__cookie-list">
                {cat.cookies.map((c) => (
                  <li key={c.name}>
                    <code>{c.name}</code> — {c.provider}, {c.purpose} ({c.expiry})
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="cookie-modal__footer">
          <button type="button" className="lux-btn-secondary" onClick={onSave}>
            {ui.savePreferences ?? 'Save preferences'}
          </button>
          <button type="button" className="lux-btn-primary" onClick={onAcceptAll}>
            {ui.acceptAll ?? 'Accept all'}
          </button>
        </footer>
      </div>
    </div>
  );
}
